import { fetchJson } from '@backoffice-frontend/common'
import {
  Environment,
  getEnvironment,
  isAdminPanel,
} from '@backoffice-frontend/environment'
import {
  Button,
  ButtonGroup,
  Callout,
  Heading,
  MiniTable,
  MiniTableRow,
  Switch,
  Text,
} from '@moia-dev/pace-web'
import type { ReactNode } from 'react'
import { useState } from 'react'
import { FEATURE_APP, Features } from './Features'
import { useFeatureToggles } from './useFeatureToggles'
import { useIsFeatureToggleEnabled } from './useIsFeatureToggleEnabled'

const getFeatureTranslation = (feature: Features) => {
  switch (feature) {
    case Features.FEATURE_TOGGLE_TEST:
      return 'Feature to test Feature Toggles'
    case Features.ALWAYS_SHOW_ENVIRONMENT_INDICATOR:
      return 'Always show environment indicator'
    case Features.MUNICH_DEMO:
      return 'All features developed for the Munich demo'
    case Features.DSBOSS_SCENARIOS_TABLE_PACE:
      return 'DSBOSS scenarios table pace'
    case Features.GI_AUTOMATED_AVOIDANCE_AREA:
      return 'GI automated avoidance area'
    case Features.SAFETY_DRIVER_ANNOTATIONS_POSTGRES:
      return 'Safety driver annotations: use Postgres instead of Redshift'
    default:
      return feature
  }
}

export const FeatureToggle = ({
  children,
  feature,
  replacement,
}: {
  children: ReactNode
  feature: Features
  replacement?: ReactNode
}) => {
  const isEnabled = useIsFeatureToggleEnabled(feature)
  return <>{isEnabled ? children : (replacement ?? null)}</>
}

export const FeatureTogglesView = () => {
  const { features: enabledFeatures, setFeatures } = useFeatureToggles()
  const [throwError, setThrowError] = useState(false)

  const handleCreateError = () => {
    setThrowError(true)
  }

  const handleToggleFeature = (name: Features) => {
    const newFeatures = enabledFeatures.includes(name)
      ? enabledFeatures.filter(feature => feature !== name)
      : [...enabledFeatures, name]

    setFeatures(newFeatures)
  }

  if (throwError) {
    throw new Error(
      'Account area error, intentionally thrown for testing purposes.',
    )
  }

  return (
    <>
      <div
        css={theme => ({
          padding: `${theme.space.Space2} ${theme.space.Space4}`,
        })}
      >
        <Heading size="lg">Enable features</Heading>
        <Text color="reduced">
          Enable unreleased features that are currently not released
        </Text>
      </div>
      <FeatureToggle feature={Features.FEATURE_TOGGLE_TEST}>
        <div css={theme => ({ padding: theme.space.Space4 })}>
          <Callout hideDismiss variant="success">
            Test feature toggle works
          </Callout>
        </div>
      </FeatureToggle>

      <MiniTable>
        {Object.values(Features)
          .filter(feature => {
            const app = FEATURE_APP[feature]
            return (
              app === 'shared' ||
              (isAdminPanel ? app === 'admin-panel' : app === 'backoffice')
            )
          })
          .map(feature => {
            const labelId = `feature-label-${feature}`
            const switchId = `feature-switch-${feature}`

            return (
              <MiniTableRow
                key={feature}
                label={getFeatureTranslation(feature)}
                content={
                  <>
                    {/* Visually hidden to enable testing/a11y. Inspired MOIA-127206 to find a cleaner solution. */}
                    <label
                      id={labelId}
                      htmlFor={switchId}
                      css={{
                        position: 'absolute',
                        width: '1px',
                        height: '1px',
                        padding: 0,
                        margin: '-1px',
                        overflow: 'hidden',
                        clip: 'rect(0, 0, 0, 0)',
                        whiteSpace: 'nowrap',
                        border: 0,
                      }}
                    >
                      {getFeatureTranslation(feature)}
                    </label>
                    <Switch
                      onChange={() => handleToggleFeature(feature)}
                      checked={enabledFeatures.includes(feature)}
                      ariaLabelledby={labelId}
                    />
                  </>
                }
              />
            )
          })}
      </MiniTable>
      <div
        css={theme => ({
          padding: theme.space.Space2,
          display: 'flex',
          justifyContent: 'flex-end',
        })}
      >
        <ButtonGroup>
          <Button
            label="Throw an error"
            onClick={handleCreateError}
            variant="secondary"
            size="compact"
          />
          {getEnvironment() === Environment.local && (
            <Button
              label="Reset Mock Server"
              variant="secondary"
              size="compact"
              onClick={() => {
                void fetchJson('http://localhost:8081/api/reset', {
                  method: 'PATCH',
                })
              }}
            />
          )}
        </ButtonGroup>
      </div>
    </>
  )
}
